import React, {
    Children,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
    CancelModal,
    HelixLoader,
    LandingPageWrapperWithLeftTabs,
} from 'components';
import InvalidPermissionsDialog from 'components/invalid-permissions-dialog/InvalidPermissionsDialog';
import {
    ConfirmationModalConfig,
    DynamicMenuItem,
} from 'components/landing-page-wrapper-with-left-tabs/components/HeaderWithActionButtons';
import { PIPELINES_AND_STEPS_PATHS } from 'constants/pipelines-and-steps-paths';
import {
    useAlert,
    useAuth,
    usePermission,
    useServicesEnabled,
} from 'core/contexts';
import {
    PutFieldMappingStepProps,
    PutJSTransformStepProps,
    PutRuleSetStepProps,
    RuleStepResponse,
    StepListResponse,
    useDeleteFieldMapping,
    useDeleteJavaScriptTransform,
    useDeleteRuleSet,
    useFieldMapping,
    useJSTransform,
    useRules,
    useRuleSet,
    useStep,
    useStepsList,
    useUpdateFieldMapping,
    useUpdateJSTransform,
    useUpdateRuleSet,
} from 'hooks';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { styles as confirmationModalStyles } from '../../../components/confirmation-modal/confirmation-modal-helper';
import {
    commonStyles,
    FIELD_MAPPING,
    JAVASCRIPT_TRANSFORM,
    RULE_SET,
    STEPS,
    StepsType,
} from '../pipelines-helper';
import {
    JavascriptTransformForm,
    SortableSearchListItemTable,
    StepDetailsForm,
} from './components';
import {
    checkForDuplicateStepName,
    CreateStepDefaults,
    CreateStepFormData,
    CreateStepSchema,
} from './components/step-form-helper';
import {
    ADD_BTN_INDEX,
    DETAILS,
    DETAILS_TAB_INDEX,
    getAccordionHeading,
    getInitialTabs,
    STEP_TYPE_TAB_INDEX,
    transformFieldMappingInbounds,
    transformRulesData,
    transformToListItems,
    TRIAL_RUN_BTN_INDEX,
    updateStepStyles,
} from './create-or-update-step-helper';
import { commonShowAlert, validationsMessages } from 'lib';
import {
    ItemType,
    SortableListItemDataWithAction,
} from './components/sortable-search-list-item-helper';
import { FieldMappingForm } from './createFieldMapping/FieldMappingForm';
import { CreateRuleForm } from './createRuleSet/CreateRuleForm';
import { Box, Grid, Typography } from '@mui/material';
import {
    CreateJavascriptTransformDefaults,
    CreateJavascriptTransformSchema,
    JavascriptTransformFormValues,
} from './createJavascriptTransform/create-javascript-transform-form-helper';
import { TrialRunJsonDataEditor, TrialRunModal } from '../components';
import { sortableSearchListItemTableStyles } from './components/sortable-search-list-item-table-helper';

export const UpdateStep = (): React.JSX.Element => {
    const { getRoles, getToken } = useAuth();
    const { isServiceEnabledFor } = useServicesEnabled();
    const isDataPipelineServiceEnabled = isServiceEnabledFor('DATA_PIPELINE');
    const { hasPermissions } = usePermission();
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const stepType = queryParams.get('option') ?? '';
    const { showAlert, hideAlert } = useAlert();
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const [readOnly, setReadOnly] = useState<boolean>(false);
    const [selectedTab, setSelectedTab] = useState<string>(DETAILS);
    const [tabNavItems, setTabNavItems] = useState(() =>
        getInitialTabs(stepType ?? '', readOnly, setDisplayPermissionDialog)
    );
    const [isChangesUnsaved, setIsChangesUnsaved] = useState<boolean>(false);
    const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
    const [originalSortableListData, setOriginalSortableListData] = useState<
        Array<Record<string, string>>
    >([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const saveActionRef = useRef<
        'save' | 'saveAndClose' | 'saveAndOpenTrialRun' | null
    >(null);
    const [stepDetails, setStepDetails] = useState<StepListResponse>();
    const [currentStepRuleSet, setCurrentStepRuleSet] = useState<
        RuleStepResponse[]
    >([]);
    const [isFieldMappingModalOpen, setIsFieldMappingModalOpen] =
        useState(false);
    const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
    const [fieldMappingToBeUpdated, setFieldMappingToBeUpdated] =
        useState<Record<string, string> | null>(null);
    const [ruleToBeUpdated, setRuleToBeUpdated] =
        useState<RuleStepResponse | null>(null);
    const [openTrialRunModal, setOpenTrialRunModal] = useState<boolean | null>(
        null
    );
    const [openDeleteConfirmationModal, setOpenDeleteConfirmationModal] =
        useState<boolean>(false);
    const [isTabsInitialized, setIsTabsInitialized] = useState(true);
    const isJsFormInitialized = useRef(false);
    const {
        data: fieldMappingDetails,
        isFetched: isFieldMappingDetailsFetched,
        isSuccess: isFieldMappingDetailsSuccess,
        isLoading: isFieldMappingDetailsLoading,
        refetch: refetchFieldMappingDetails,
    } = useFieldMapping({ id: id as string, token: getToken() }, stepType);
    const {
        data: ruleSetDetails,
        isFetched: isRuleSetDetailsFetched,
        isSuccess: isRuleSetDetailsSuccess,
        isLoading: isRuleSetDetailsLoading,
        refetch: refetchRuleSetDetails,
    } = useRuleSet({ id: id as string, token: getToken() }, stepType);
    const {
        data: rulesList,
        isLoading: isRulesListLoading,
        refetch: refetchRulesList,
    } = useRules(stepType);
    const {
        data: jsTransformDetails,
        isFetched: isJSTransformDetailsFetched,
        isSuccess: isJSTransformDetailsSuccess,
        isLoading: isJSTransformDetailsLoading,
        refetch: refetchJSTransformDetails,
    } = useJSTransform({ id: id as string, token: getToken() }, stepType);
    const {
        data: stepsListData,
        isLoading: isStepsListDataLoading,
        refetch: refetchStepsListData,
    } = useStepsList(isDataPipelineServiceEnabled);
    const {
        control,
        reset,
        getValues,
        watch,
        handleSubmit,
        formState: { errors },
    } = useForm<CreateStepFormData>({
        mode: 'all',
        resolver: yupResolver(CreateStepSchema),
        defaultValues: CreateStepDefaults,
    });
    const { mutate: updateStepFieldMapping } = useUpdateFieldMapping();
    const { mutate: updateStepRuleSet } = useUpdateRuleSet();
    const { mutate: updateJavascriptTransform } = useUpdateJSTransform();

    const { mutate: deleteStepFieldMapping } = useDeleteFieldMapping();
    const { mutate: deleteStepRuleSet } = useDeleteRuleSet();
    const { mutate: deleteJavascriptTransform } =
        useDeleteJavaScriptTransform();

    const type =
        StepsType.find((step) => step.label === stepType)?.type ??
        StepsType[2].type;
    const { data: stepTypeListData, refetch: refetchStepTypeListData } =
        useStep({ type });
    const name = watch('name');
    const {
        control: jsTransformControl,
        reset: jsTransformReset,
        watch: jsTransformWatch,
        setValue: jsTransformSetValue,
        clearErrors: jsTransformClearErrors,
        getValues: jsTransformGetValues,
        setError: jsTransformSetError,
        formState: { isDirty: jsTransformIsDirty, errors: jsTransformErrors },
    } = useForm<JavascriptTransformFormValues>({
        mode: 'all',
        resolver: yupResolver(CreateJavascriptTransformSchema),
        defaultValues: CreateJavascriptTransformDefaults,
    });
    const funcName = jsTransformWatch('funcName');
    const jsBase64 = jsTransformWatch('jsBase64');

    useEffect(() => {
        if (getRoles().includes('sdzAdmin')) {
            setReadOnly(false);
        } else {
            setReadOnly(true);
        }
        refetchStepsListData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (tabNavItems && isTabsInitialized) {
            setTimeout(() => setIsTabsInitialized(false), 1000);
        }
    }, [tabNavItems]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (
            stepType === JAVASCRIPT_TRANSFORM &&
            jsTransformDetails &&
            !isJsFormInitialized.current
        ) {
            jsTransformReset({
                funcName: jsTransformDetails?.funcName || '',
                jsBase64: atob(jsTransformDetails?.jsBase64) || '',
            });
            isJsFormInitialized.current = true;
        }
    }, [jsTransformDetails]); // eslint-disable-line react-hooks/exhaustive-deps

    const setDetailsTab = (stepData: StepListResponse): void => {
        tabNavItems[DETAILS_TAB_INDEX] = {
            ...tabNavItems[DETAILS_TAB_INDEX],
            tabContent: (
                <StepDetailsForm
                    control={control}
                    errors={errors}
                    stepDetailsFormValues={stepData}
                    handleUnsavedChanges={(): void => {
                        setIsChangesUnsaved(true);
                    }}
                />
            ),
        };
        setTabNavItems([...tabNavItems]);
    };

    const removeFieldMapping = (data: Record<string, string>): void => {
        setOriginalSortableListData((prev) => {
            const updated = prev.filter((item) => item !== data);
            if (updated.length !== prev.length) {
                setIsChangesUnsaved(true);
            }
            return updated;
        });
    };

    const removeRuleSet = (index: number): void => {
        const allRulesOfStep = [...currentStepRuleSet];
        allRulesOfStep.splice(index, 1);
        setCurrentStepRuleSet(allRulesOfStep);
        setOriginalSortableListData(transformRulesData(allRulesOfStep));
        setIsChangesUnsaved(true);
    };

    const handleActionsClick = (
        dataWithAction: SortableListItemDataWithAction,
        index: number
    ): void => {
        if (stepType === FIELD_MAPPING) {
            if (dataWithAction.action === 'edit') {
                setIsFieldMappingModalOpen(true);
                setFieldMappingToBeUpdated(dataWithAction.data);
            } else if (dataWithAction.action === 'delete') {
                removeFieldMapping(dataWithAction.data);
            }
        }
        if (stepType === RULE_SET) {
            if (dataWithAction.action === 'edit') {
                setIsRuleModalOpen(true);
                const ruleFound = currentStepRuleSet?.find(
                    (rule) =>
                        rule.name === dataWithAction.data.name &&
                        rule.description === dataWithAction.data.description
                );
                if (ruleFound) {
                    setRuleToBeUpdated(ruleFound);
                }
            } else if (dataWithAction.action === 'delete') {
                removeRuleSet(index);
            }
        }
    };

    const tabContent = useMemo(() => {
        return originalSortableListData.length > 0 ? (
            <SortableSearchListItemTable
                data={originalSortableListData}
                type={stepType as ItemType}
                handleActionsClick={(
                    dataWithAction: SortableListItemDataWithAction,
                    index: number
                ): void => handleActionsClick(dataWithAction, index)}
            />
        ) : (
            <Box sx={updateStepStyles.emptyFieldMappingContainer}>
                <Grid sx={updateStepStyles.emptyStateContainer}>
                    <Grid>
                        <ErrorOutlineIcon
                            sx={updateStepStyles.errorIconStyles}
                        />
                    </Grid>
                    <Grid>
                        <Typography sx={updateStepStyles.emptyHeadingText}>
                            The Step is Empty
                        </Typography>
                    </Grid>
                    <Grid>
                        <Typography sx={updateStepStyles.emptyBodyText}>
                            A step cannot be empty. Add a new{' '}
                            {stepType === FIELD_MAPPING
                                ? ' field mapping '
                                : ' rule '}{' '}
                            using the button in the top right corner to
                            continue.
                        </Typography>
                    </Grid>
                </Grid>
            </Box>
        );
    }, [originalSortableListData, stepType]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!funcName) {
            jsTransformSetError('funcName', {
                type: 'required',
                message: validationsMessages.required,
            });
        }

        tabNavItems[STEP_TYPE_TAB_INDEX].isError = !(funcName && jsBase64);
        setTabNavItems([...tabNavItems]);

        const isJsBase64Changed =
            jsBase64 !== atob(jsTransformDetails?.jsBase64 ?? '');
        if (jsTransformIsDirty || isJsBase64Changed) {
            setIsChangesUnsaved(true);
        }
    }, [funcName, jsBase64]); // eslint-disable-line react-hooks/exhaustive-deps

    const tabContentForJSTransform = (): React.JSX.Element => {
        return (
            <Box sx={updateStepStyles.jsTransformConatiner}>
                <JavascriptTransformForm
                    control={jsTransformControl}
                    errors={jsTransformErrors}
                    watch={jsTransformWatch}
                    setValue={jsTransformSetValue}
                    clearErrors={jsTransformClearErrors}
                    setError={jsTransformSetError}
                />
            </Box>
        );
    };

    const handleTrialRunClick = (): void => {
        if (readOnly) {
            setDisplayPermissionDialog(true);
            return;
        }
        if (isChangesUnsaved) {
            setOpenTrialRunModal(false);
            return;
        }
        setOpenTrialRunModal(true);
    };

    const setStepTypeTab = useCallback((): void => {
        const updatedTabNavItems = [...tabNavItems];

        if (stepType === JAVASCRIPT_TRANSFORM) {
            const headerButtons = [
                ...(updatedTabNavItems[STEP_TYPE_TAB_INDEX].tabHeaderConfig
                    ?.headerButtonsConfig ?? []),
            ];

            if (headerButtons[0]) {
                headerButtons[0] = {
                    ...headerButtons[0],
                    onClick: (): void => handleTrialRunClick(),
                };
            }
            updatedTabNavItems[STEP_TYPE_TAB_INDEX] = {
                ...updatedTabNavItems[STEP_TYPE_TAB_INDEX],
                tabContent: tabContentForJSTransform(),
                tabHeaderConfig: {
                    headingText:
                        updatedTabNavItems[STEP_TYPE_TAB_INDEX].tabHeaderConfig
                            ?.headingText ?? '',
                    headerButtonsConfig: headerButtons,
                },
            };
        } else if (stepType === FIELD_MAPPING || stepType === RULE_SET) {
            tabNavItems[STEP_TYPE_TAB_INDEX].isError =
                stepType === FIELD_MAPPING
                    ? originalSortableListData.length < 1
                    : currentStepRuleSet.length < 1;
            updatedTabNavItems[STEP_TYPE_TAB_INDEX] = {
                ...updatedTabNavItems[STEP_TYPE_TAB_INDEX],
                tabContent: tabContent,
            };

            const headerButtons = [
                ...(updatedTabNavItems[STEP_TYPE_TAB_INDEX].tabHeaderConfig
                    ?.headerButtonsConfig ?? []),
            ];

            if (headerButtons[ADD_BTN_INDEX]) {
                headerButtons[ADD_BTN_INDEX] = {
                    ...headerButtons[ADD_BTN_INDEX],
                    onClick: (): void => {
                        if (stepType === FIELD_MAPPING) {
                            setFieldMappingToBeUpdated(null);
                            setIsFieldMappingModalOpen(true);
                        } else if (stepType === RULE_SET) {
                            setRuleToBeUpdated(null);
                            setIsRuleModalOpen(true);
                        }
                    },
                };
            }

            if (headerButtons[TRIAL_RUN_BTN_INDEX]) {
                headerButtons[TRIAL_RUN_BTN_INDEX] = {
                    ...headerButtons[TRIAL_RUN_BTN_INDEX],
                    onClick: (): void => handleTrialRunClick(),
                };
            }

            updatedTabNavItems[STEP_TYPE_TAB_INDEX] = {
                ...updatedTabNavItems[STEP_TYPE_TAB_INDEX],
                tabHeaderConfig: {
                    headingText:
                        updatedTabNavItems[STEP_TYPE_TAB_INDEX].tabHeaderConfig
                            ?.headingText ?? '',
                    headerButtonsConfig: headerButtons,
                },
            };
        }

        setTabNavItems(updatedTabNavItems);
    }, [
        tabNavItems,
        stepType,
        originalSortableListData,
        funcName,
        jsBase64,
        isChangesUnsaved,
    ]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDetailsTab = (
        stepName: string,
        stepDescription: string
    ): void => {
        const stepFound = stepsListData?.find((step) => step.id === id);
        reset({
            name: stepName ?? '',
            description: stepDescription ?? '',
        });
        if (stepFound) {
            setStepDetails(stepFound);
            setDetailsTab(stepFound);
        }
    };

    useEffect(() => {
        if (fieldMappingDetails) {
            handleDetailsTab(
                fieldMappingDetails.name,
                fieldMappingDetails.description
            );
            setOriginalSortableListData(
                transformFieldMappingInbounds(fieldMappingDetails.map)
            );
        }
    }, [
        fieldMappingDetails,
        isFieldMappingDetailsFetched,
        isFieldMappingDetailsSuccess,
        stepsListData,
    ]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (ruleSetDetails) {
            handleDetailsTab(ruleSetDetails.name, ruleSetDetails.description);
            if (rulesList) {
                const rulesMatched = ruleSetDetails?.ruleIds
                    .map((ruleId) =>
                        rulesList.find(
                            (rule: RuleStepResponse) => rule.id === ruleId
                        )
                    )
                    .filter(
                        (rule): rule is RuleStepResponse => rule !== undefined
                    );
                setCurrentStepRuleSet(rulesMatched);
                setOriginalSortableListData(transformRulesData(rulesMatched));
            }
        }
    }, [
        ruleSetDetails,
        isRuleSetDetailsFetched,
        isRuleSetDetailsSuccess,
        stepsListData,
        rulesList,
    ]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (jsTransformDetails) {
            handleDetailsTab(
                jsTransformDetails.name,
                jsTransformDetails.description
            );
        }
    }, [
        jsTransformDetails,
        isJSTransformDetailsFetched,
        isJSTransformDetailsSuccess,
        stepsListData,
    ]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    useEffect(() => {
        setStepTypeTab();
    }, [originalSortableListData, funcName, jsBase64, isChangesUnsaved]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (name) {
            tabNavItems[DETAILS_TAB_INDEX].isError = false;
            setTabNavItems([...tabNavItems]);
        }
    }, [name]); // eslint-disable-line react-hooks/exhaustive-deps

    const onTabChange = (
        event: React.SyntheticEvent,
        newValue: string
    ): void => {
        if (event) {
            if (newValue === DETAILS) {
                setSelectedTab(newValue);
                if (stepDetails) {
                    const stepDetailsData: StepListResponse = {
                        ...stepDetails,
                        name: getValues('name'),
                        description: getValues('description'),
                    };
                    setDetailsTab(stepDetailsData);
                }
            } else {
                setSelectedTab(newValue);
                if (!name) {
                    tabNavItems[DETAILS_TAB_INDEX].isError = true;
                }
                setStepTypeTab();
            }
        }
    };

    const navigateToStepsTab = (): void => {
        navigate(`${PIPELINES_AND_STEPS_PATHS.LANDING_PAGE}?option=${STEPS}`);
    };

    const navigateToStepsTabWithDelay = (): void => {
        setTimeout(() => {
            navigateToStepsTab();
        }, 500);
    };

    const handleDeleteStep = (): void => {
        if (stepType === FIELD_MAPPING) {
            deleteStepFieldMapping(
                { id: id as string, token: getToken() as string },
                {
                    onSuccess: () => {
                        navigateToStepsTabWithDelay();
                    },
                }
            );
        } else if (stepType === RULE_SET) {
            deleteStepRuleSet(
                { id: id as string, token: getToken() as string },
                {
                    onSuccess: () => {
                        navigateToStepsTabWithDelay();
                    },
                }
            );
        } else if (stepType === JAVASCRIPT_TRANSFORM) {
            deleteJavascriptTransform(
                { id: id as string, token: getToken() as string },
                {
                    onSuccess: () => {
                        navigateToStepsTabWithDelay();
                    },
                }
            );
        }
    };

    const handleDeleteConfirmationModal = (): void => {
        const permission: boolean = hasPermissions(['deleteMetadata']);
        if (!permission) {
            setDisplayPermissionDialog(true);
            return;
        }
        setOpenDeleteConfirmationModal(true);
    };

    const getDeleteConfirmationModalData = (): any => {
        if (stepType === FIELD_MAPPING) {
            return fieldMappingDetails;
        } else if (stepType === RULE_SET) {
            return ruleSetDetails;
        } else if (stepType === JAVASCRIPT_TRANSFORM) {
            return jsTransformDetails;
        }

        return undefined;
    };

    const handleOpenModal = (modalType: 'cancel' | 'delete'): void => {
        if (modalType === 'cancel') {
            setOpenCancelModal(false);
        } else if (modalType === 'delete') {
            setOpenDeleteConfirmationModal(false);
        }
        setIsChangesUnsaved(false);
    };

    const handleConfirmationModalConfig = (): ConfirmationModalConfig => {
        const data = getDeleteConfirmationModalData();

        return {
            confirmationModal: openDeleteConfirmationModal,
            confirmationKey: data?.name ?? '',
            confirmationButtonText: 'Delete Step',
            hasConfirmationTextField: true,
            confirmationCancelButtonText: 'Cancel',
            confirmationHeadingText: 'Confirm Step Deletion',
            confirmationInputLabelText: `Type ${data?.name} below`,
            confirmationSubHeadingText: (
                <Typography>
                    By typing{' '}
                    <Typography
                        component='span'
                        sx={confirmationModalStyles.textBoldStyles}
                    >
                        ‘{data?.name}’
                    </Typography>{' '}
                    below, you understand that you are permanently deleting this
                    step. This action{' '}
                    <Typography
                        component='span'
                        sx={confirmationModalStyles.textBoldStyles}
                    >
                        CANNOT
                    </Typography>{' '}
                    be undone.
                </Typography>
            ),
            handleConfirmation: () => handleDeleteStep(),
            handleConfirmationModal: () => handleOpenModal('delete'),
        };
    };

    const menuItems: DynamicMenuItem[] = [
        {
            label: 'Delete Step',
            icon: <DeleteIcon sx={commonStyles.deleteIconStyles} />,
            onClick: handleDeleteConfirmationModal,
        },
    ];

    const handleCancelModalConfirmation = (): void => {
        setOpenCancelModal(false);
        navigateToStepsTab();
    };

    const handleCancelButton = (): void => {
        if (isChangesUnsaved) {
            setOpenCancelModal(true);
        } else {
            navigateToStepsTab();
        }
    };

    const handleAfterSaveRefetch = (action: string): void => {
        if (stepType === FIELD_MAPPING) {
            refetchFieldMappingDetails();
        } else if (stepType === RULE_SET) {
            refetchRuleSetDetails();
            refetchRulesList();
        } else if (stepType === JAVASCRIPT_TRANSFORM) {
            refetchJSTransformDetails();
        }
        refetchStepTypeListData();
        refetchStepsListData();
        setIsLoading(false);
        if (action === 'saveAndOpenTrialRun') {
            setTimeout(() => {
                setOpenTrialRunModal(true);
            }, 500);
        }
    };

    const handleUpdateSuccess = (): void => {
        tabNavItems[DETAILS_TAB_INDEX].isError = false;
        setIsChangesUnsaved(false);

        if (saveActionRef.current === 'saveAndClose') {
            setIsLoading(false);
            setTimeout(() => {
                navigateToStepsTab();
            }, 1000);
        } else if (saveActionRef.current === 'save') {
            handleAfterSaveRefetch('save');
        } else if (saveActionRef.current === 'saveAndOpenTrialRun') {
            setOpenTrialRunModal(null);
            handleAfterSaveRefetch('saveAndOpenTrialRun');
        }

        saveActionRef.current = null;
    };

    const handleUpdateError = (): void => {
        saveActionRef.current = null;
        setIsLoading(false);
        setIsChangesUnsaved(true);
    };

    const prepareFieldMappingProps = (
        data: CreateStepFormData
    ): PutFieldMappingStepProps => {
        const normalizedList = originalSortableListData.map((item) => {
            const key = Object.keys(item)[0];
            const value = Object.values(item)[0];
            return { [key]: value === 'None' ? '' : value };
        });

        const putData: PutFieldMappingStepProps = {
            id: id as string,
            name: data.name,
            description: data.description,
            map: Object.assign({}, ...normalizedList) ?? {},
        };
        return putData;
    };

    const updateFieldMapping = (putData: PutFieldMappingStepProps): void => {
        updateStepFieldMapping(
            {
                token: getToken() as string,
                putData,
            },
            {
                onSuccess: (): void => handleUpdateSuccess(),
                onError: (): void => handleUpdateError(),
            }
        );
    };

    const prepareRuleSetProps = (
        data: CreateStepFormData
    ): PutRuleSetStepProps => {
        const putData: PutRuleSetStepProps = {
            id: id as string,
            name: data.name,
            description: data.description,
            ruleIds: currentStepRuleSet?.map((rule) => rule.id) ?? [],
        };
        return putData;
    };

    const updateRuleSet = (putData: PutRuleSetStepProps): void => {
        updateStepRuleSet(
            {
                token: getToken() as string,
                putData,
            },
            {
                onSuccess: (): void => handleUpdateSuccess(),
                onError: (): void => handleUpdateError(),
            }
        );
    };

    const prepareJSTransformProps = (
        data: CreateStepFormData
    ): PutJSTransformStepProps => {
        const putData: PutJSTransformStepProps = {
            id: id as string,
            name: data.name,
            description: data.description,
            funcName: jsTransformGetValues()?.funcName ?? '',
            jsBase64: btoa(jsTransformGetValues()?.jsBase64 ?? ''),
        };
        return putData;
    };

    const updateJSTransform = (putData: PutJSTransformStepProps): void => {
        updateJavascriptTransform(
            {
                token: getToken() as string,
                putData,
            },
            {
                onSuccess: (): void => handleUpdateSuccess(),
                onError: (): void => handleUpdateError(),
            }
        );
    };

    const handleDuplicateCheck = (
        data: any[],
        putData:
            | PutFieldMappingStepProps
            | PutRuleSetStepProps
            | PutJSTransformStepProps
    ): boolean => {
        const idx = checkForDuplicateStepName(data ?? [], putData);
        if (idx) {
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                'A step with this name already exists.'
            );
            setIsLoading(false);
            return true;
        }
        return false;
    };

    const emptyStepState = (): boolean => {
        if (
            (stepType === FIELD_MAPPING || stepType === RULE_SET) &&
            originalSortableListData.length === 0
        ) {
            setIsLoading(false);
            commonShowAlert(showAlert, hideAlert, 'error', 'Unable to save');
            return true;
        }

        if (
            stepType === JAVASCRIPT_TRANSFORM &&
            (jsTransformGetValues()?.funcName === '' ||
                jsTransformGetValues()?.jsBase64 === '')
        ) {
            setIsLoading(false);
            commonShowAlert(showAlert, hideAlert, 'error', 'Unable to save');
            return true;
        }
        return false;
    };

    const onSubmit = (data: CreateStepFormData): void => {
        if (!data) return;
        setIsLoading(true);

        if (emptyStepState()) return;

        if (stepType === FIELD_MAPPING) {
            const putData = prepareFieldMappingProps(data);
            if (!handleDuplicateCheck(stepTypeListData?.data ?? [], putData)) {
                updateFieldMapping(putData);
            }
        }

        if (stepType === RULE_SET) {
            const putData = prepareRuleSetProps(data);
            if (!handleDuplicateCheck(stepTypeListData?.data ?? [], putData)) {
                updateRuleSet(putData);
            }
        }

        if (stepType === JAVASCRIPT_TRANSFORM) {
            const putData = prepareJSTransformProps(data);
            if (!handleDuplicateCheck(stepTypeListData?.data ?? [], putData)) {
                updateJSTransform(putData);
            }
        }
    };

    const handleSaveButtonClick = (
        actionType: 'save' | 'saveAndClose' | 'saveAndOpenTrialRun'
    ): void => {
        const permission: boolean = hasPermissions(['editMetadata']);
        if (!permission) {
            setDisplayPermissionDialog(true);
            return;
        }
        saveActionRef.current = actionType;
        if (!name) {
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                'Step must have a name.'
            );
            return;
        }
        handleSubmit(onSubmit)();
    };

    const onFieldMappingModalClose = (): void => {
        setIsFieldMappingModalOpen(false);
    };

    const onRuleModalClose = (): void => {
        setIsRuleModalOpen(false);
    };

    const onAddNewOrUpdateFieldMapping = (
        map: Record<string, string>
    ): void => {
        let originalList = [...originalSortableListData];
        let updatedMap: Record<string, string | null> = map;
        if (fieldMappingToBeUpdated) {
            const [[updateKey, updateValue]] = Object.entries(
                fieldMappingToBeUpdated
            );
            originalList = originalSortableListData.filter((item) => {
                const [[itemKey, itemValue]] = Object.entries(item);
                return !(itemKey === updateKey && itemValue === updateValue);
            });
            setFieldMappingToBeUpdated(null);
        }
        if (Object.values(map)[0] === '') {
            updatedMap = {
                [Object.keys(map)[0]]: 'None',
            };
        }
        const sortedData = transformFieldMappingInbounds(
            Object.assign({}, updatedMap, ...originalList)
        );
        setOriginalSortableListData(sortedData);
        setIsChangesUnsaved(true);
    };

    const onAddNewOrUpdateRule = (ruleData: RuleStepResponse): void => {
        const {
            name: ruleName,
            description,
            whenBase64,
            thenBase64,
        } = ruleData;
        let updatedList = [...originalSortableListData];

        if (ruleToBeUpdated) {
            const updatedRuleValues = {
                ...ruleToBeUpdated,
                name: ruleName,
                description,
                whenBase64,
                thenBase64,
            };

            updatedList = updatedList.filter(
                (item) =>
                    !(
                        item.name === ruleToBeUpdated.name &&
                        item.description === ruleToBeUpdated.description
                    )
            );

            setRuleToBeUpdated(updatedRuleValues);
        } else {
            setIsChangesUnsaved(true);
        }

        setCurrentStepRuleSet((prev) => {
            const exists = prev.some((rule) => rule.id === ruleData.id);

            if (exists) {
                return prev.map((rule) =>
                    rule.id === ruleData.id ? { ...rule, ...ruleData } : rule
                );
            }

            return [...prev, ruleData];
        });

        updatedList.push({ name: ruleName, description });

        setOriginalSortableListData(updatedList);
        setRuleToBeUpdated(null);
    };

    const handleTrialRunModalCloseButton = (): void => {
        setOpenTrialRunModal(null);
    };

    const handleSaveAndContinueButton = (): void => {
        setIsLoading(true);
        handleSaveButtonClick('saveAndOpenTrialRun');
    };

    const handleCloseModal = (): void => {
        setOpenTrialRunModal(null);
    };

    if (!isDataPipelineServiceEnabled) {
        navigate(PIPELINES_AND_STEPS_PATHS.LANDING_PAGE);
        return <></>;
    }

    if (
        isFieldMappingDetailsLoading ||
        isRuleSetDetailsLoading ||
        isStepsListDataLoading ||
        isRulesListLoading ||
        isJSTransformDetailsLoading ||
        isTabsInitialized
    ) {
        return <HelixLoader />;
    }

    return (
        <>
            <LandingPageWrapperWithLeftTabs
                includeBreadcrumbs={true}
                breadCrumLastItem={stepDetails?.name ?? ''}
                headingText={stepDetails?.name ?? ''}
                isLoading={isLoading}
                isChangesUnsaved={isChangesUnsaved}
                openCancelModal={openCancelModal}
                handleCancelButton={handleCancelButton}
                handleCancelModal={(): void => handleOpenModal('cancel')}
                handleCancelModalConfirmation={handleCancelModalConfirmation}
                handleSaveAndCloseButton={(): void =>
                    handleSaveButtonClick('saveAndClose')
                }
                handleSaveButton={(): void => handleSaveButtonClick('save')}
                isMenuComponentVisible={true}
                menuItems={menuItems}
                tabItems={tabNavItems}
                selectedTab={selectedTab}
                onTabChange={onTabChange}
                tabsContainerStyles={commonStyles.tabsContainerStyles}
                tabsStyles={commonStyles.tabsStyles}
                tabItemStyles={{ ...commonStyles.tabItemStyles, p: '2px 12px' }}
                confirmationModalConfig={handleConfirmationModalConfig()}
            />
            {isFieldMappingModalOpen && (
                <FieldMappingForm
                    isEdit={!!fieldMappingToBeUpdated}
                    onModalClose={onFieldMappingModalClose}
                    onAddNewOrUpdateFieldMapping={onAddNewOrUpdateFieldMapping}
                    {...(fieldMappingToBeUpdated
                        ? { fieldMappingDetails: fieldMappingToBeUpdated }
                        : {})}
                />
            )}
            {isRuleModalOpen && (
                <CreateRuleForm
                    isEdit={!!ruleToBeUpdated}
                    onModalClose={onRuleModalClose}
                    onAddNewOrUpdateRule={onAddNewOrUpdateRule}
                    {...(ruleToBeUpdated
                        ? { ruleDetails: ruleToBeUpdated }
                        : {})}
                />
            )}
            {openTrialRunModal && (
                <TrialRunModal
                    open={openTrialRunModal}
                    handleCloseButton={handleTrialRunModalCloseButton}
                    trialRunId={id as string}
                    trialRunType={stepType}
                    trialRunPageHeading={`${stepType} Trial Run`}
                    accordionConfig={{
                        heading: getAccordionHeading(stepType),
                        children:
                            stepType === JAVASCRIPT_TRANSFORM ? (
                                <TrialRunJsonDataEditor
                                    control={jsTransformControl}
                                    value={jsBase64}
                                    readOnlyEditor={true}
                                    codeMirrorHeight='264px'
                                />
                            ) : (
                                <Grid
                                    sx={{
                                        ...sortableSearchListItemTableStyles.listContainerStyles,
                                        ...updateStepStyles.trialRunAccordionContainer,
                                    }}
                                >
                                    {Children.toArray(
                                        transformToListItems(
                                            originalSortableListData,
                                            stepType
                                        )?.map((item) => item.item)
                                    )}
                                </Grid>
                            ),
                    }}
                />
            )}
            {openTrialRunModal === false && (
                <CancelModal
                    cancelModal={!openTrialRunModal}
                    handleCancelModal={handleCloseModal}
                    cancelButtonText='Save and continue'
                    cancelHeadingText='You have unsaved changes.'
                    cancelSubHeadingText='This step has unsaved changes. You must save changes before you can perform a trial run.'
                    cancelModalText='Go Back'
                    handleCancel={handleSaveAndContinueButton}
                />
            )}
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </>
    );
};
